<template>
  <el-dialog
    :title="$t('smartRouting.deliver.danceDeliver')"
    v-model="visible"
    destroy-on-close
    :modal="true"
    :append-to-body="true"
    width="820px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <!-- 步骤条 -->
    <el-steps :active="step" align-center finish-status="success" class="deliver-steps">
      <el-step :title="$t('smartRouting.deliver.stepSelectDevice')" />
      <el-step :title="$t('smartRouting.deliver.stepPrecheck')" />
      <el-step :title="$t('smartRouting.deliver.stepConfigDeliver')" />
    </el-steps>

    <!-- ===================== 步骤1：选择目标设备 ===================== -->
    <div v-if="step === 0" class="step-content">
      <!-- 提示条 -->
      <div class="tip-bar">
        {{ $t('smartRouting.deliver.tipStep1', { name: danceInfo.name, title: '舞种' }) }}
        <span class="file-name">{{ danceInfo.fileName }}</span>
      </div>

      <!-- 下发策略 -->
      <div class="strategy-section">
        <span class="section-title">{{ $t('smartRouting.deliver.strategy') }}</span>
        <el-radio-group v-model="strategy" size="small">
          <el-radio-button label="FULL">{{$t('smartRouting.mapCenter.pathPlanning.routePackageDelivery.strategyFull')}}</el-radio-button>
          <!-- <el-radio-button disabled label="GRAY">{{$t('smartRouting.mapCenter.pathPlanning.routePackageDelivery.strategyGray')}}</el-radio-button>
          <el-radio-button disabled label="INCR">{{$t('smartRouting.mapCenter.pathPlanning.routePackageDelivery.strategyIncremental')}}</el-radio-button> -->
        </el-radio-group>
      </div>

      <!-- 目标设备 -->
      <div class="section-title">
        {{ $t('smartRouting.deliver.targetDevice') }}
        <span class="device-count">
          {{ $t('smartRouting.deliver.deviceCount', {
            selected: selectedDevices?.length || 0,
            total: deviceList?.length || 0
          }) }}
          <span v-if="faultCount">
            {{ $t('smartRouting.deliver.deviceCountFault', { fault: faultCount }) }}
          </span>
          {{ $t('smartRouting.deliver.deviceCountEnd') }}
        </span>
      </div>

      <el-table
        ref="deviceTableRef"
        :data="deviceList"
        height="280"
        @selection-change="handleDeviceChange"
        class="section-table"
      >
        <el-table-column type="selection" />
        <el-table-column prop="deviceCode" :label="$t('smartRouting.deliver.columnSn')" />
        <el-table-column prop="deviceName" :label="$t('smartRouting.deliver.columnDeviceName')"  />
        <el-table-column prop="deviceModel" :label="$t('smartRouting.deliver.columnDeviceModel')" />
        <el-table-column prop="isOnline" :label="$t('smartRouting.deliver.columnStatus')" width="120">
          <template #default="{ row }">
            <el-tag :type="statusTagMap[row.isOnline] || ''" size="small">
              {{ row.isOnline
                ? $t('smartRouting.deliver.statusOnline')
                : $t('smartRouting.deliver.statusOffline') }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- ===================== 步骤2：部署预检 ===================== -->
    <div v-else-if="step === 1" class="step-content">
      <div class="tip-bar">
        {{ $t('smartRouting.deliver.tipStep2', {
          name: danceInfo.name,
          count: selectedDevices?.length || 0
        }) }}
      </div>

      <div v-if="prechecking" class="precheck-loading">
        <i class="el-icon-loading" />
        <span>{{ $t('smartRouting.deliver.prechecking') }}</span>
      </div>

      <div v-else class="precheck-list">
        <div class="precheck-item">
          <i
            :class="precheckList.onlineDevices.length !== 0 ? 'el-icon-success' : 'el-icon-error'"
            :style="{ color: precheckList.onlineDevices.length !== 0 ? '#67c23a' : '#f56c6c' }"
            class="precheck-icon"
          />
          <div class="precheck-info">
            <div class="precheck-title">{{ $t('smartRouting.deliver.precheckTitleOnlineDevices') }}</div>
            <div class="precheck-desc">
              {{ $t('smartRouting.deliver.precheckDescOnlineDevices', { count: precheckList.onlineDevices.length }) }}
            </div>
          </div>
        </div>
        <div class="precheck-item">
          <i
            :class="precheckList.onlineDevices.length !== 0 ? 'el-icon-success' : 'el-icon-error'"
            :style="{ color: precheckList.onlineDevices.length !== 0 ? '#67c23a' : '#f56c6c' }"
            class="precheck-icon"
          />
          <div class="precheck-info">
            <div class="precheck-title">{{ $t('smartRouting.deliver.precheckTitleDanceCompatibility') }}</div>
            <div class="precheck-desc">{{ $t('smartRouting.deliver.precheckDescDanceCompatibility') }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== 步骤3：配置下发 ===================== -->
    <div v-else-if="step === 2" class="step-content">
      <div class="tip-bar">
        {{ delivering
          ? $t('smartRouting.deliver.tipStep3Delivering', { name: danceInfo.name, count: selectedDevices?.length || 0 })
          : $t('smartRouting.deliver.tipStep3Delivered', { name: danceInfo.name, count: selectedDevices?.length || 0 }) }}
      </div>

      <!-- 进度条 -->
      <el-progress
        :percentage="deliverProgress"
        :status="deliverProgress === 100 ? 'success' : ''"
        :stroke-width="12"
        class="deliver-progress"
      />

      <div class="deliver-status">
        <template v-if="deliverProgress < 100">
          <i class="el-icon-loading" />
          <span>{{ $t('smartRouting.deliver.inProgress') }}</span>
        </template>
        <template v-else>
          <i class="el-icon-success" style="color: #67c23a;" />
          <span>{{ $t('smartRouting.deliver.completed') }}</span>
        </template>
      </div>

      <!-- 下发完成结果 -->
      <div v-if="deliverProgress === 100" class="deliver-result">
        <div class="result-title">
          <i class="el-icon-success" style="color: #67c23a;" />
          {{ $t('smartRouting.deliver.completed') }}
        </div>
        <div class="result-desc">
          {{ $t('smartRouting.deliver.resultDesc', {
            targets: selectedDevices.map((d) => d.deviceName).join('、'),
            count: selectedDevices?.length || 0,
            fileName: danceInfo.fileName
          }) }}
        </div>
      </div>
    </div>

    <!-- ===================== 底部按钮 ===================== -->
    <template #footer>
      <!-- 步骤1 -->
      <template v-if="step === 0">
        <el-button size="mini" @click="handleClose">
          {{ $t('smartRouting.deliver.btnCancel') }}
        </el-button>
        <el-button
          type="danger"
          size="mini"
          :disabled="(selectedDevices?.length || 0) === 0"
          @click="handleNext"
        >
          {{ $t('smartRouting.deliver.btnNext') }}
        </el-button>
      </template>

      <!-- 步骤2 -->
      <template v-else-if="step === 1">
        <el-button size="mini" :disabled="prechecking" @click="step = 0">
          {{ $t('smartRouting.deliver.btnPrev') }}
        </el-button>
        <el-button
          type="danger"
          size="mini"
          :loading="prechecking"
          @click="handleConfirmDeliver"
        >
          {{ $t('smartRouting.deliver.btnConfirm') }}
        </el-button>
      </template>

      <!-- 步骤3 -->
      <template v-else>
        <el-button type="danger" size="mini" @click="handleClose">
          {{ $t('smartRouting.deliver.btnFinish') }}
        </el-button>
      </template>
    </template>
  </el-dialog>
</template>

<script>
import { ref, computed, nextTick, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { reqDeviceList } from '@/api/map'
import { reqDeployPrecheck, reqDeployDance } from '@/api/dance'
import { useI18n } from 'vue-i18n'

export default {
  name: 'DanceDeliverDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    // 当前下发的舞种信息 { id, name, fileName, fileUrl, code }
    danceInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update:modelValue', 'success'],
  setup(props, { emit }) {
    // ===== 弹窗显隐 =====
    const visible = computed({
      get: () => props.modelValue,
      set: (val) => emit('update:modelValue', val),
    })

    // ===== i18n =====
    const { t } = useI18n()

    // ===== 状态映射（isOnline 为布尔值） =====
    const statusTagMap = {
      true: 'success',
      false: 'info',
    }

    // ===== 步骤 =====
    const step = ref(0)

    // ===== 设备列表 =====
    const deviceList = ref([])
    const deviceTableRef = ref(null)
    const selectedDevices = ref([])
    const strategy = ref('FULL')

    // 是否在线（兼容布尔 / 字符串 / 数字）
    const checkOnline = (val) =>
      val === true || val === 'online' || val === 1 || val === '1'

    // 故障设备数量（离线数量）
    const faultCount = computed(
      () => (deviceList.value || []).filter((d) => !checkOnline(d.isOnline)).length
    )

    // 是否可选：仅在线设备可选
    const isSelectable = (row) => checkOnline(row.isOnline)

    const handleDeviceChange = (rows) => {
      selectedDevices.value = rows || []
    }

    // ===== 获取设备列表 =====
    const fetchDeviceList = async () => {
      try {
        const res = await reqDeviceList({})
        // 兼容多种返回结构：res 直接是数组 / res.data 是数组 / res.list 是数组
        const list = Array.isArray(res)
          ? res
          : res?.data || res?.list || []
        deviceList.value = Array.isArray(list) ? list : []
      } catch (error) {
        console.error('getDevicesFailed:', error)
        ElMessage.error(t('smartRouting.deliver.getDevicesFailed'))
        deviceList.value = []
      }
    }

    // 弹窗打开时加载设备列表
    watch(
      () => props.modelValue,
      (val) => {
        if (val) {
          fetchDeviceList()
        }
      },
      { immediate: true }
    )

    // ===== 预检列表 =====
    const precheckList = ref({})
    const prechecking = ref(false)

    // ===== 下发进度 =====
    const delivering = ref(false)
    const deliverProgress = ref(0)

    // ===== 构造通用请求参数 =====
    const buildDeployParams = () => ({
      resourceCode: props.danceInfo.code,
      resourceType: 'DANCE',
      targetDevices: (selectedDevices.value || []).map((d) => d.deviceCode),
    })

    // ===== 下一步：执行预检 =====
    const handleNext = async () => {
      if (step.value === 0) {
        try {
          await runPrecheck()
          step.value = 1
        } catch (e) {
          // 预检失败时不进入下一步
        }
      }
    }

    // ===== 执行预检（真实接口） =====
    const runPrecheck = async () => {
      prechecking.value = true
      try {
        const res = await reqDeployPrecheck(buildDeployParams())
        precheckList.value = res
      } catch (error) {
        console.error('precheckFailed:', error)
        ElMessage.error(t('smartRouting.deliver.precheckFailed'))
        precheckList.value = {}
        throw error
      } finally {
        prechecking.value = false
      }
    }

    // ===== 确认下发（真实接口 + 进度动画） =====
    const handleConfirmDeliver = async () => {
      step.value = 2
      delivering.value = true
      deliverProgress.value = 0

      try {
        await doDeploy()
      } catch (error) {
        delivering.value = false
        ElMessage.error(t('smartRouting.deliver.failed'))
      }
    }

    // ===== 执行下发 =====
    const doDeploy = async () => {
      const params = {
        danceCode: props.danceInfo.code,
        targetDevices: precheckList.value.onlineDevices,
        strategy: "FULL"
      }

      // 启动进度动画（若接口支持进度查询，可改为轮询真实进度）
      const timer = setInterval(() => {
        if (deliverProgress.value < 90) {
          deliverProgress.value += 10
        }
      }, 200)

      try {
        const res = await reqDeployDance(params)
        clearInterval(timer)
        deliverProgress.value = 100
        delivering.value = false

        // 通知父组件刷新列表
        emit('success', {
          danceId: props.danceInfo.id,
          deviceIds: (selectedDevices.value || []).map((d) => d.deviceCode),
          result: res,
        })

        ElMessage.success(t('smartRouting.deliver.success'))
        return res
      } catch (error) {
        clearInterval(timer)
        deliverProgress.value = 0
        delivering.value = false
        throw error
      }
    }

    // ===== 关闭 =====
    const handleClose = () => {
      visible.value = false
      setTimeout(() => {
        step.value = 0
        selectedDevices.value = []
        deviceTableRef.value?.clearSelection()
        precheckList.value = {}
        prechecking.value = false
        deliverProgress.value = 0
        delivering.value = false
        deviceList.value = []
      }, 300)
    }

    return {
      visible,
      step,
      deviceList,
      deviceTableRef,
      selectedDevices,
      faultCount,
      strategy,
      isSelectable,
      handleDeviceChange,
      fetchDeviceList,
      precheckList,
      prechecking,
      delivering,
      deliverProgress,
      statusTagMap,
      handleNext,
      handleConfirmDeliver,
      handleClose,
    }
  },
}
</script>

<style lang="scss" scoped>
.deliver-steps {
  margin-bottom: 20px;
}

.step-content {
  min-height: 360px;
}

.tip-bar {
  background: #ecf5ff;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 13px;
  color: #303133;
  line-height: 1.6;
  margin-bottom: 16px;

  .file-name {
    color: #409eff;
    font-weight: 500;
  }
}

.strategy-section {
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  .device-count {
    font-weight: 400;
    color: #909399;
    font-size: 12px;
  }
}

.section-table{
  margin-top: 18px;
}

.section-value {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
}

/* ===== 预检 ===== */
.precheck-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 30px 0;
  justify-content: center;
  color: #909399;
  font-size: 13px;
}

.precheck-list {
  .precheck-item {
    display: flex;
    align-items: flex-start;
    padding: 14px 16px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    margin-bottom: 10px;

    .precheck-icon {
      font-size: 18px;
      margin-right: 10px;
      margin-top: 1px;
    }

    .precheck-info {
      flex: 1;

      .precheck-title {
        font-size: 13px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 4px;
      }

      .precheck-desc {
        font-size: 12px;
        color: #909399;
        line-height: 1.5;
      }
    }
  }
}

/* ===== 下发 ===== */
.deliver-progress {
  margin: 16px 0 10px;
}

.deliver-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #67c23a;
  margin-bottom: 16px;
}

.deliver-result {
  background: #f5f7fa;
  border-radius: 4px;
  padding: 14px 16px;
  margin-top: 10px;

  .result-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 6px;
  }

  .result-desc {
    font-size: 12px;
    color: #909399;
    line-height: 1.6;
  }
}
</style>